// src/pages/modals/report/ScheduledReportCreateModal.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Input, Row, Col, Label, Badge, Alert
} from "reactstrap";
import axios from "axios";
import { API } from "../../../api/urls";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { ACCOUNT_TYPES } from "../../../constants/accountTypes";

// Kurumsal renkler
const NAVY = "#221C5D";
const ORANGE = "#E56A22";

const REPORT_OPTIONS = {
    STATUS: [
        { key: "SUCCESS", label: "Başarılı" },
        { key: "PENDING", label: "Beklemede" },
        { key: "FAILED",  label: "Başarısız" },
    ],
};

const ScheduledReportCreateModal = ({ isOpen, toggle, onSaved }) => {
    const [scheduleType, setScheduleType]   = useState("DAILY");
    const [scheduleDay, setScheduleDay]     = useState("");
    const [minAmount, setMinAmount]         = useState("");
    const [accountId, setAccountId]         = useState("");
    const REPORT_ACCOUNT_TYPES = ACCOUNT_TYPES.map(t => ({
        key: t.value,
        label: t.label,
    }));
    const [statuses, setStatuses]         = useState(REPORT_OPTIONS.STATUS.map(o => o.key));
    const [accountTypes, setAccountTypes] = useState(REPORT_ACCOUNT_TYPES.map(o => o.key));
    const [companyId, setCompanyId]         = useState("");

    const [saving, setSaving]               = useState(false);
    const [user, setUser]                   = useState(null);
    const [accounts, setAccounts]           = useState([]);
    const [companies, setCompanies]         = useState([]);
    const [loading, setLoading]             = useState({ accounts: false, companies: false, user: false });
    const [error, setError]                 = useState({ accounts: null, companies: null, user: null });

    const needsDay = scheduleType === "MONTHLY" || scheduleType === "QUARTERLY";
    const isSuperAdmin = Array.isArray(user?.roles) && user.roles.includes("ROLE_SUPER_ADMIN");

    const toggleInArray = (arr, value, setter) => {
        if (arr.includes(value)) setter(arr.filter(v => v !== value));
        else setter([...arr, value]);
    };

    const showToast = (type, msg) => (toast[type] ? toast[type](msg) : toast(msg));

    // SA ise seçili companyId'ye göre cari daralt
    const filteredAccounts = useMemo(() => {
        if (isSuperAdmin && companyId) {
            return accounts.filter(acc => String(acc.companyId) === String(companyId));
        }
        return accounts;
    }, [accounts, companyId, isSuperAdmin]);

    useEffect(() => {
        if (!isSuperAdmin || !accountId) return;
        const stillExists = filteredAccounts.some(acc => String(acc.id) === String(accountId));
        if (!stillExists) setAccountId("");
    }, [companyId, filteredAccounts, isSuperAdmin]); // eslint-disable-line

    const fetchInitialData = async () => {
        setLoading(prev => ({ ...prev, user: true, accounts: true, companies: false }));
        setError(prev => ({ ...prev, user: null, accounts: null, companies: null }));

        const authHeader = { headers: {} };

        try {
            // USER
            const userResp = await axios.get(API.auth_me, authHeader);
            if (userResp?.data?.statusCode === 200 && userResp.data.data) {
                const u = userResp.data.data;
                setUser(u);
                setCompanyId(u.companyId || "");
                setLoading(prev => ({ ...prev, user: false }));

                // COMPANIES (SA ise)
                if (Array.isArray(u.roles) && u.roles.includes("ROLE_SUPER_ADMIN")) {
                    setLoading(prev => ({ ...prev, companies: true }));
                    try {
                        const compResp = await axios.get(API.companies_list, authHeader);
                        if (compResp?.data?.statusCode === 200 && Array.isArray(compResp.data?.data)) {
                            setCompanies(compResp.data.data);
                        } else {
                            setError(prev => ({ ...prev, companies: "Şirket listesi alınamadı." }));
                        }
                    } catch (e) {
                        setError(prev => ({
                            ...prev,
                            companies: e.response?.data?.message || "Şirket listesi alınırken hata oluştu."
                        }));
                    } finally {
                        setLoading(prev => ({ ...prev, companies: false }));
                    }
                }
            } else {
                setError(prev => ({ ...prev, user: "Kullanıcı bilgisi alınamadı." }));
                setLoading(prev => ({ ...prev, user: false, companies: false }));
            }
        } catch (e) {
            setError(prev => ({
                ...prev,
                user: e.response?.data?.message || "Kullanıcı bilgisi alınırken hata oluştu."
            }));
            setLoading(prev => ({ ...prev, user: false, companies: false }));
        }

        // ACCOUNTS
        try {
            const accResp = await axios.get(API.accounts_me, authHeader);
            if (accResp?.data?.statusCode === 200 && Array.isArray(accResp.data.data)) {
                setAccounts(accResp.data.data);
            } else {
                setError(prev => ({ ...prev, accounts: "Cari listesi alınamadı." }));
            }
        } catch (e) {
            setError(prev => ({
                ...prev,
                accounts: e.response?.data?.message || "Cari listesi alınırken hata oluştu."
            }));
        } finally {
            setLoading(prev => ({ ...prev, accounts: false }));
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchInitialData();
            setStatuses(REPORT_OPTIONS.STATUS.map(o => o.key));
            setAccountTypes(REPORT_ACCOUNT_TYPES.map(o => o.key));
            setScheduleType("DAILY");
            setScheduleDay("");
            setMinAmount("");
            setAccountId("");
        }
    }, [isOpen]);

    const save = async () => {
        try {
            setSaving(true);
            const payload = {
                companyId: isSuperAdmin ? (companyId || null) : null,
                scheduleType,
                scheduleDay: needsDay ? (scheduleDay ? Number(scheduleDay) : null) : null,
                minAmount: minAmount !== "" ? Number(minAmount) : null,
                accountId: accountId || null,
                statuses,
                accountTypes,
            };

            const { data } = await axios.post(API.scheduled_reports_list, payload);

            if (data?.statusCode === 200) {
                onSaved?.();
                toggle();
                showToast("success", "Rapor başarıyla kaydedildi!");
            } else {
                showToast("error", data?.message || "Kayıt başarısız");
            }
        } catch (e) {
            const msg =
                e.response?.data?.message ||
                (e.message?.includes("Network") ? "Ağ hatası. Lütfen bağlantınızı kontrol edin." : e.message) ||
                "Kayıt sırasında hata oluştu";
            showToast("error", msg);
        } finally {
            setSaving(false);
        }
    };

    // ---- Styles: CompaniesTable ile aynı dil ----
    const styles = `
    .sr-modal .modal-content { border: 0; border-radius: 1rem; box-shadow: 0 .75rem 2rem rgba(10,49,110,.12); }
    .sr-modal .modal-header { border: 0; padding-bottom: .25rem; }
    .sr-modal .modal-title  { font-weight: 700; color: #222; }
    .sr-modal .modal-body   { padding-top: .5rem; }

    .sr-modal .pill-input, .sr-modal .pill-select {
      border-radius: 999px !important;
      padding: .45rem .9rem;
      height: 40px;
      font-size: .95rem;
    }
    .sr-modal .section {
      border: 1px solid #eef1f5;
      border-radius: .85rem;
      padding: .9rem;
      background: #fff;
    }
    .sr-modal .section + .section { margin-top: .75rem; }

    .sr-modal .hint {
      background: #f8fbff;
      border: 1px dashed ${NAVY}33;
      color: #4c607a;
      border-radius: .85rem;
    }

    .sr-modal .btn-rounded { border-radius: 999px; }
    .sr-modal .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .sr-modal .btn-navy:hover { filter: brightness(1.03); }
    .sr-modal .btn-orange { background: ${ORANGE}; border-color: ${ORANGE}; color: #fff; }
    .sr-modal .btn-orange:hover { filter: brightness(1.03); }

    .sr-modal .pill-badge {
      border-radius: 999px;
      padding: .4rem .65rem;
      user-select: none;
      transition: transform .05s ease;
      cursor: pointer;
    }
    .sr-modal .pill-badge:active { transform: scale(.98); }

    .sr-modal .mini-actions .btn { height: 32px; padding: .25rem .6rem; }

    @media (max-width: 576px) {
      .sr-modal .pill-input, .sr-modal .pill-select { height: 38px; font-size: .95rem; }
    }
  `;

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered className="sr-modal">
            <style>{styles}</style>

            {/* Toasts */}
            <ToastContainer position="top-center" newestOnTop toastStyle={{ zIndex: 2147483647 }} />

            <ModalHeader toggle={toggle} className="bg-white">
                Zamanlanmış Rapor Oluştur
            </ModalHeader>

            <ModalBody>
                <Alert color="info" className="py-2 hint">
                    <ul className="mb-0 ps-3">
                        <li>Rapor e-postası yalnızca <strong>kendi e-postanıza</strong> gönderilir.</li>
                        <li>
                            <strong>Zamanlama</strong> seçin. Aylık veya 3 Aylık ise
                            <strong> Gün (1–28)</strong> alanını <em>(opsiyonel)</em> girebilirsiniz.
                        </li>
                    </ul>
                </Alert>

                {/* Şirket seçimi (SA) */}
                {isSuperAdmin && (
                    <div className="section">
                        <Row className="g-3">
                            <Col md="12">
                                <Label className="mb-1">Şirket</Label>
                                <Input
                                    type="select"
                                    value={companyId}
                                    onChange={(e) => setCompanyId(e.target.value)}
                                    disabled={loading.companies}
                                    className="pill-select"
                                >
                                    <option value="">Şirket Seçiniz</option>
                                    {companies.map(comp => (
                                        <option key={comp.id} value={comp.id}>{comp.companyName}</option>
                                    ))}
                                </Input>
                                {loading.companies && <small className="text-muted">Şirket listesi yükleniyor…</small>}
                                {error.companies && <small className="text-danger d-block">{error.companies}</small>}
                            </Col>
                        </Row>
                    </div>
                )}

                {/* Zamanlama & Gün */}
                <div className="section">
                    <Row className="g-3">
                        <Col md="6">
                            <Label className="mb-1">Zamanlama</Label>
                            <Input
                                type="select"
                                value={scheduleType}
                                onChange={(e) => setScheduleType(e.target.value)}
                                className="pill-select"
                            >
                                <option value="DAILY">Günlük</option>
                                <option value="WEEKLY">Haftalık</option>
                                <option value="MONTHLY">Aylık</option>
                                <option value="QUARTERLY">3 Aylık</option>
                            </Input>
                        </Col>

                        <Col md="6">
                            <Label className="mb-1">Gün (1–28) {needsDay ? "(opsiyonel)" : "(devre dışı)"}</Label>
                            <Input
                                type="number" min="1" max="28"
                                value={scheduleDay}
                                onChange={(e) => setScheduleDay(e.target.value)}
                                disabled={!needsDay}
                                placeholder="örn. 15"
                                className="pill-input"
                            />
                        </Col>
                    </Row>
                </div>

                {/* Tutar & Cari */}
                <div className="section">
                    <Row className="g-3">
                        <Col md="6">
                            <Label className="mb-1">Min Tutar (₺)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={minAmount}
                                onChange={(e) => setMinAmount(e.target.value)}
                                placeholder="örn. 1000"
                                className="pill-input"
                            />
                        </Col>

                        <Col md="6">
                            <Label className="mb-1">Cari/Bayi (opsiyonel)</Label>
                            <Input
                                type="select"
                                value={accountId}
                                onChange={(e) => setAccountId(e.target.value)}
                                disabled={loading.accounts || (isSuperAdmin && !companyId)}
                                className="pill-select"
                            >
                                <option value="">Tüm Cariler</option>
                                {filteredAccounts.map(acc => (
                                    <option key={acc.id} value={acc.id}>
                                        {(acc.accountName || acc.title || "-")}{acc.accountCode ? ` (${acc.accountCode})` : ""}
                                    </option>
                                ))}
                            </Input>
                            {isSuperAdmin && !companyId && (
                                <small className="text-muted">Cari seçebilmek için önce bir şirket seçin.</small>
                            )}
                            {loading.accounts && <small className="text-muted d-block">Cari listesi yükleniyor…</small>}
                            {error.accounts && <small className="text-danger d-block">{error.accounts}</small>}
                        </Col>
                    </Row>
                </div>

                {/* Durum & Cari Türleri */}
                <div className="section">
                    <Row className="g-3">
                        <Col md="12">
                            <div className="d-flex justify-content-between align-items-center">
                                <Label className="mb-0">Durum Filtreleri</Label>
                                <div className="mini-actions d-flex gap-2">
                                    <Button size="sm" className="btn-rounded btn-orange" onClick={() => setStatuses(REPORT_OPTIONS.STATUS.map(o => o.key))}>
                                        Tümünü Seç
                                    </Button>
                                    <Button size="sm" className="btn-rounded btn-light" onClick={() => setStatuses([])}>
                                        Temizle
                                    </Button>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-2 mt-2">
                                {REPORT_OPTIONS.STATUS.map(({ key, label }) => {
                                    const active = statuses.includes(key);
                                    return (
                                        <Badge
                                            key={key}
                                            className={`pill-badge ${active ? "text-white" : ""}`}
                                            color={active ? "primary" : "light"}
                                            onClick={() => toggleInArray(statuses, key, setStatuses)}
                                        >
                                            {label}
                                        </Badge>
                                    );
                                })}
                            </div>
                        </Col>

                        <Col md="12">
                            <div className="d-flex justify-content-between align-items-center mt-2">
                                <Label className="mb-0">Cari Türleri</Label>
                                <div className="mini-actions d-flex gap-2">
                                    <Button
                                        size="sm"
                                        className="btn-rounded btn-orange"
                                        onClick={() => setAccountTypes(REPORT_ACCOUNT_TYPES.map(o => o.key))}
                                    >
                                        Tümünü Seç
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="btn-rounded btn-light"
                                        onClick={() => setAccountTypes([])}
                                    >
                                        Temizle
                                    </Button>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-2 mt-2">
                                {REPORT_ACCOUNT_TYPES.map(({ key, label }) => {
                                    const active = accountTypes.includes(key);
                                    return (
                                        <Badge
                                            key={key}
                                            className={`pill-badge ${active ? "text-white" : ""}`}
                                            color={active ? "info" : "light"}
                                            onClick={() => toggleInArray(accountTypes, key, setAccountTypes)}
                                        >
                                            {label}
                                        </Badge>
                                    );
                                })}
                            </div>
                        </Col>
                    </Row>
                </div>
            </ModalBody>

            <ModalFooter className="bg-white">
                <Button color="light" className="btn-rounded" onClick={toggle} disabled={saving}>
                    Vazgeç
                </Button>
                <Button className="btn-rounded btn-navy" onClick={save} disabled={saving}>
                    {saving ? "Kaydediliyor..." : "Kaydet"}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ScheduledReportCreateModal;